import { FC, useCallback, useEffect, useState } from 'react';
import { styled } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  DISABLED_TOOLTIP,
  FOUR_O_NINE_A_REPORT,
  NO_INFORMATION_AVAILABLE_FOR_REPORT_DATE,
} from 'dashboard409a/common/constants';
import { Collapse, Valuation, VisibilityToggle, Waterfall } from 'dashboard409a/components';
import { AccordionReportHeader } from 'dashboard409a/components/AccordionReportHeader';
import { Disclaimer } from 'dashboard409a/components/Disclaimer';
import { PublicComps } from 'dashboard409a/components/PublicComps';
import { VisibilityToggleType } from 'dashboard409a/components/VisibilityToggle/VisibilityToggle.props';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext/CompanyDashboardContext';
import {
  IValuationState,
  togglePublicCompsVisibility,
  toggleValuationVisibility,
} from 'dashboard409a/states/features/valuation';
import { IWaterfallState, loadWaterfall, toggleWaterfallVisibility } from 'dashboard409a/states/features/waterfall';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import EmptyReportDataMessage from './EmptyReportDataMessage';

const PREFIX = 'Overview';

const classes = {
  waterfallTitle: `${PREFIX}-waterfallTitle`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled('div')(() => ({
  [`& .${classes.waterfallTitle}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
}));

const Overview: FC = () => {
  const [isValuationCollapseOpen, setIsValuationCollapseOpen] = useState(true);
  const { currentCompany } = useSelector((state: RootStateType) => state.global);
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const { editMode } = useCompanyDashboardContext();

  const isSuperuser = user?.is_superuser;
  const isStaff = user?.is_staff;
  const isAnalyst = isSuperuser || isStaff;

  const dispatch = useDispatch<AppDispatchType>();

  const { currentMeasurementDateId, currentCompanyMeasurementDateId } = useSelector(
    (state: RootStateType) => state.global
  );
  const { waterfallBreakpoint, isWaterfallVisible, currentCaptable } = useSelector<RootStateType, IWaterfallState>(
    state => state.waterfall
  );
  const { currentValuationScalarOpinion, isValuationVisible, isPublicCompsVisible } = useSelector<
    RootStateType,
    IValuationState
  >(state => state.valuation);

  const isApprovedForClients = currentValuationScalarOpinion?.approved_for_clients ?? false;
  const isCompanyUserAndIsNotApprovedForClients = !isSuperuser && !isStaff && !isApprovedForClients;
  const shouldShowWaterfall = waterfallBreakpoint && (isAnalyst || isWaterfallVisible);
  const shouldShowValuation = isAnalyst || isValuationVisible;
  const shouldShowPublicComps = isAnalyst || isPublicCompsVisible;

  const handleToggleWaterfallVisibility = useCallback(
    (visible: boolean) => {
      if (currentCaptable?.id) {
        dispatch(
          toggleWaterfallVisibility({
            captableId: currentCaptable.id,
            visible,
          })
        );
      }
    },
    [dispatch, currentCaptable]
  );

  const handleTogglePublicCompsVisibility = useCallback(
    (visible: boolean) => {
      if (!currentCompanyMeasurementDateId) {
        return;
      }

      dispatch(
        togglePublicCompsVisibility({
          measurementDateId: currentCompanyMeasurementDateId,
          visible,
        })
      );
    },
    [dispatch, currentCompanyMeasurementDateId]
  );

  const handleToggleValuationVisibility = useCallback(
    (visible: boolean) => {
      if (!currentCompanyMeasurementDateId) {
        return;
      }

      dispatch(
        toggleValuationVisibility({
          measurementDateId: currentCompanyMeasurementDateId,
          visible,
        })
      );
    },
    [dispatch, currentCompanyMeasurementDateId]
  );

  const renderNoReportDataMessage = () => <EmptyReportDataMessage title={NO_INFORMATION_AVAILABLE_FOR_REPORT_DATE} />;

  useEffect(() => {
    if (currentCompany?.id && currentMeasurementDateId) {
      dispatch(
        loadWaterfall({
          companyId: currentCompany.id,
          measurementDateId: currentMeasurementDateId,
        })
      );
    }
  }, [currentCompany?.id, currentMeasurementDateId, dispatch]);

  if (!currentValuationScalarOpinion?.report_date) {
    return renderNoReportDataMessage();
  }

  return (
    <Root>
      {/* Page-level report header, above the section cards (redesign). */}
      <AccordionReportHeader reportName={FOUR_O_NINE_A_REPORT} />
      {shouldShowValuation && (
        <>
          {isAnalyst && editMode && (
            <div data-tour="valuation-visibility-toggle" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VisibilityToggle
                visible={isValuationVisible}
                onChange={handleToggleValuationVisibility}
                tooltipTitle="Toggle visibility for company users"
                type={VisibilityToggleType.BAR}
              />
            </div>
          )}
          <Collapse
            title="Valuation"
            disabled={isCompanyUserAndIsNotApprovedForClients}
            disabledTooltip={DISABLED_TOOLTIP}
            isOpen={isValuationCollapseOpen}
            onToggle={setIsValuationCollapseOpen}>
            <Valuation />
          </Collapse>
        </>
      )}
      {shouldShowPublicComps && (
        <>
          {isAnalyst && editMode && (
            <div data-tour="public-comps-visibility-toggle" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VisibilityToggle
                visible={isPublicCompsVisible}
                onChange={handleTogglePublicCompsVisibility}
                tooltipTitle="Toggle visibility for company users"
                type={VisibilityToggleType.BAR}
              />
            </div>
          )}
          <Collapse
            title="Public Comps"
            disabled={isCompanyUserAndIsNotApprovedForClients}
            disabledTooltip={DISABLED_TOOLTIP}>
            <PublicComps />
          </Collapse>
        </>
      )}
      {shouldShowWaterfall && (
        <>
          {isAnalyst && editMode && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <VisibilityToggle
                visible={isWaterfallVisible}
                onChange={handleToggleWaterfallVisibility}
                tooltipTitle="Toggle visibility for company users"
                type={VisibilityToggleType.BAR}
              />
            </div>
          )}
          <Collapse
            title={<div className={classes.waterfallTitle}>Waterfall</div>}
            disabled={isCompanyUserAndIsNotApprovedForClients}
            disabledTooltip={DISABLED_TOOLTIP}>
            <Waterfall />
          </Collapse>
        </>
      )}
      <Disclaimer />
    </Root>
  );
};

export default Overview;
